import React from "react";
import { useIntl } from "react-intl";
import { gql, useMutation } from "@apollo/client";
import { MaterialColor } from "@syos-music/instruments";
import { Product } from "@syos-music/products";

import {
  Box,
  CircularProgress,
  Tooltip,
  Stack,
  IconButton,
} from "@mui/material";
import {
  PackageUp as RemoveIcon,
  BilliardsRack as OriginalsIcon,
  Draw as SignatureIcon,
} from "mdi-material-ui";

const OriginalsType = Product.displayModes.originalsMouthpiece.OriginalsType;

const REMOVE_MUTATION = gql`
  mutation ($inventoryId: ID!) {
    RemoveInventory(inventoryId: $inventoryId) {
      id
    }
  }
`;

function parseSku(sku) {
  const [
    type,
    instrument,
    instrumentType,
    typeSpec,
    tipOpening,
    mouthpieceColor,
  ] = sku.split("|");

  let parsed = {
    type,
    instrument,
    instrumentType,
    tipOpening,
    mouthpieceColor,
  };

  if (type === "originals") parsed.originalsType = typeSpec;
  if (type === "signature") parsed.signatureMouthpieceId = typeSpec;

  return parsed;
}

function TooltipTitle({ entry }) {
  const intl = useIntl();

  const { type, originalsType, tipOpening, mouthpieceColor } = parseSku(
    entry.sku
  );

  const [remove, { loading }] = useMutation(REMOVE_MUTATION, {
    variables: { inventoryId: entry.id },
  });

  return (
    <Stack
      spacing={1}
      direction="row"
      justifyContent="center"
      alignItems="center"
    >
      <span>
        {[
          type === "originals" && OriginalsType.asString(intl, originalsType),
          type === "signature" && entry.signatureMouthpiece.signature,

          tipOpening,
          MaterialColor.asString(intl, mouthpieceColor),
        ]
          .filter(Boolean)
          .join(" - ")}
      </span>

      <IconButton
        size="small"
        color="inherit"
        onClick={remove}
        disabled={loading}
      >
        {loading ? (
          <CircularProgress size={20} sx={{ color: "#fff" }} />
        ) : (
          <RemoveIcon />
        )}
      </IconButton>
    </Stack>
  );
}

export default function InventoryBoxEntry({ entry }) {
  const { type, tipOpening, mouthpieceColor } = parseSku(entry.sku);

  return (
    <Tooltip
      placement="top"
      title={<TooltipTitle entry={entry} />}
      enterDelay={400}
    >
      <Stack
        spacing={1}
        direction="row"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {type === "originals" && <OriginalsIcon />}
          {type === "signature" && <SignatureIcon />}
        </Box>

        <Box
          sx={{
            width: 12,
            height: 24,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {tipOpening}
        </Box>

        <MaterialColor
          color={mouthpieceColor}
          variant="preview"
          sx={{ width: 24, height: 24 }}
        />
      </Stack>
    </Tooltip>
  );
}

InventoryBoxEntry.fragments = {
  inventory: gql`
    fragment InventoryBoxEntry_inventory on Inventory {
      id
      sku

      signatureMouthpiece {
        id
        signature
      }
    }
  `,
};
